import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { API } from "../lib/api";
import { SeoHead } from "../components/common/SeoHead";

const JAKARTA = { fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" };

export default function StudioAgreementPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    fetch(`${API}/studio/agreements/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error("Agreement not found or not yet issued.");
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/studio/agreements/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signed_name: name, consent }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Please complete the fields");
      setAccepted(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main style={JAKARTA} className="bg-[#F8F5EF] py-28 text-[#241b3a]" data-testid="studio-agreement-page">
      <SeoHead title="Service Agreement — IAGSB Digital Studio" description="Review and electronically accept your IAGSB Digital Studio service agreement." path={`/studio/agreement/${token}`} />
      <div className="container-site max-w-3xl">
        {error && !data && (
          <p className="rounded-2xl border border-[#c0392b]/30 bg-white p-8 text-center text-sm text-[#c0392b]" data-testid="agreement-error">{error}</p>
        )}
        {data && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#F47735]">IAGSB Digital Studio · Service Agreement</p>
            <h1 className="mt-3 text-3xl font-extrabold text-[#35205F]" data-testid="agreement-number">{data.agreement.number}</h1>
            <p className="mt-2 text-sm text-[#241b3a]/60">
              Prepared for <strong>{data.client?.business_name}</strong> · Version {data.agreement.version}
              {data.agreement.quotation_no ? ` · Quotation ${data.agreement.quotation_no}` : ""}
            </p>
            <pre className="mt-8 whitespace-pre-wrap rounded-2xl border border-[#35205F]/15 bg-white p-6 text-[13px] leading-relaxed text-[#241b3a]/85 sm:p-10" data-testid="agreement-body">
              {data.agreement.body}
            </pre>

            {accepted || data.agreement.status === "accepted" ? (
              <div className="mt-8 rounded-2xl border border-[#17A6A0]/40 bg-[#17A6A0]/10 p-6 text-center" data-testid="agreement-accepted">
                <CheckCircle2 className="mx-auto h-10 w-10 text-[#17A6A0]" strokeWidth={1.5} />
                <p className="mt-3 font-extrabold text-[#35205F]">Agreement accepted</p>
                <p className="mt-1 text-sm text-[#241b3a]/65">
                  Signed by <strong>{data.agreement.signed_name || name}</strong> · {(accepted?.accepted_at || data.agreement.accepted_at || "").replace("T", " ").slice(0, 16)} (Malaysia time) · Version {data.agreement.version}
                </p>
              </div>
            ) : (
              <div className="mt-8 rounded-2xl border border-[#35205F]/15 bg-white p-6 sm:p-8" data-testid="agreement-sign-box">
                <p className="flex items-center gap-2 font-extrabold text-[#35205F]"><ShieldCheck className="h-5 w-5 text-[#17A6A0]" strokeWidth={1.75} /> Electronic acceptance</p>
                <p className="mt-2 text-xs leading-relaxed text-[#241b3a]/60">Your acceptance is recorded with the agreement version, timestamp and audit details.</p>
                <input
                  className="mt-4 w-full rounded-xl border border-[#35205F]/20 px-4 py-3 text-sm outline-none focus:border-[#F47735]"
                  placeholder="Type your full name as signature"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="agreement-sign-name"
                />
                <label className="mt-4 flex items-start gap-3 text-sm text-[#241b3a]/75">
                  <input type="checkbox" className="mt-1 h-4 w-4 accent-[#F47735]" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="agreement-consent" />
                  <span>I have read and agree to this service agreement (version {data.agreement.version}), and I accept it electronically on behalf of {data.client?.business_name}.</span>
                </label>
                {error && <p className="mt-3 text-xs font-semibold text-[#c0392b]">{error}</p>}
                <button onClick={accept} disabled={busy || !name.trim() || !consent} data-testid="agreement-accept-btn" className="mt-5 rounded-full bg-[#F47735] px-7 py-3 text-sm font-bold text-[#241b3a] transition-transform hover:scale-[1.02] disabled:opacity-40">
                  {busy ? "Recording…" : "Accept agreement"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
